import { MouseEventHandler, useEffect, useState } from 'react';

import { MDText } from 'i18n-react';

import { Hyperlink, LoadingIndicator } from '@motorway/mw-highway-code/alpha';
import { SIZE } from '@motorway/mw-highway-code/enums';
import { useCopyToClipboard } from '@motorway/mw-highway-code/hooks';
import { Drawer } from '@motorway/mw-highway-code/pre-alpha';

import { useUserPartial } from 'Context/user';

import { MORE_FROM_MOTORWAY_EVENTS } from 'Utilities/analytics/analyticsMoreFromMotorway';

import { enrolReferrer } from 'Services/mentionMe';

import useToastNotification from 'Containers/app/common/Hooks/useToastNotification';

import { ShareLinkButtons } from './ShareLinkButtons/ShareLinkButtons';
import { buildEnrolPayload, getReferralLink } from './ReferAFriend.helpers';
import LocalTexts from './ReferAFriend.texts.json';

import styles from './ReferAFriend.module.scss';

const LocalT = new MDText(LocalTexts);

const TERMS_URL = 'https://motorway.mention-me.com/me/terms/160448/zwrerapy';

export type ReferAFriendProps = {
  isOpen: boolean;
  onClose: () => void;
  situation: string;
};

export const ReferAFriend = ({
  isOpen,
  onClose,
  situation,
}: ReferAFriendProps) => {
  const user = useUserPartial();
  const { showErrorNotification } = useToastNotification();
  const { copy, isCopied } = useCopyToClipboard();

  const [referralLink, setReferralLink] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const abortController = new AbortController();
    let isActive = true;

    const generateLink = async () => {
      setIsLoading(true);
      setReferralLink(undefined);

      try {
        const response = await enrolReferrer(
          buildEnrolPayload({ situation, user }),
          abortController,
        );

        const link = getReferralLink(response);

        if (!link) {
          throw new Error('Mention Me enrol response did not contain a referral link');
        }

        if (isActive) {
          setReferralLink(link);
          MORE_FROM_MOTORWAY_EVENTS.REFER_POPUP({ category: situation, label: link });
        }
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }

        globalThis?.Sentry?.captureException?.(
          error instanceof Error ? error : new Error(String(error)),
          { tags: { fetch: 'Mention Me enrol referrer' } },
        );

        if (isActive) {
          showErrorNotification({
            message: LocalT.translate('errorMessage') as string,
            title: LocalT.translate('errorTitle') as string,
          });
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    generateLink();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const termsLink = (
    <a data-thc-style-override href={TERMS_URL} rel="noopener noreferrer" target="_blank">
      {LocalT.translate('termsLinkText')}
    </a>
  );

  const onLinkClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    copy(referralLink ?? '');
    MORE_FROM_MOTORWAY_EVENTS.REFER_COPY({ category: situation, label: 'Referral link' });
  };

  return (
    <Drawer
      responsive
      aria-labelledby='refer-a-friend-heading'
      id='refer-a-friend-drawer'
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={styles.referAFriend}>
        <div className={styles.intro}>
          <h3 className={styles.heading} id='refer-a-friend-heading'>{LocalT.translate('heading')}</h3>
          <p data-thc-style-override className={styles.description}>{LocalT.translate('description')}</p>
        </div>

        <div
          aria-busy={isLoading}
          aria-label={isLoading ? (LocalT.translate('generatingLink') as string) : undefined}
          className={styles.linkContainer}
        >
          {isLoading && <LoadingIndicator size={SIZE.MEDIUM} />}

          {!isLoading && referralLink && (
            <>
              <p data-thc-style-override className={styles.linkLabel}>{LocalT.translate('linkLabel')}</p>
              <Hyperlink data-thc-hyperlink href={referralLink} label={referralLink} onClick={onLinkClick} />
            </>
          )}
        </div>

        {referralLink && (
          <ShareLinkButtons
            copy={copy}
            isCopied={isCopied}
            onCopy={(label) => MORE_FROM_MOTORWAY_EVENTS.REFER_COPY({ category: situation, label })}
            onShare={(label) => MORE_FROM_MOTORWAY_EVENTS.REFER_SHARE({ category: situation, label })}
            shareText={LocalT.translate('shareText') as string}
            shareTitle={LocalT.translate('heading') as string}
            url={referralLink}
          />
        )}

        <p className={styles.terms}>
          {LocalT.translate('terms', { link: termsLink })}
        </p>
      </div>
    </Drawer>
  );
};
